import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, TextInput, RadioButton, RadioButtonGroup, ProgressIndicator, ProgressStep, Checkbox } from '@carbon/react';
import { ArrowRight, ArrowLeft, Purchase, Locked, Wallet } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [shippingData, setShippingData] = useState(null);
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    saveCard: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const subtotal = cartTotal;
  const shipping = subtotal > 200 ? 0 : 9.99;
  const tax = subtotal * 0.10; // GST 10%
  const total = subtotal + shipping + tax;

  useEffect(() => {
    const saved = localStorage.getItem('chillverify-shipping');
    if (saved) {
      setShippingData(JSON.parse(saved));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const validate = () => {
    const newErrors = {};
    if (paymentMethod === 'card') {
      if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 16) newErrors.cardNumber = 'Valid card number required';
      if (!formData.cardName) newErrors.cardName = 'Cardholder name required';
      if (!formData.expiry) newErrors.expiry = 'Expiry date required';
      if (!formData.cvv || formData.cvv.length < 3) newErrors.cvv = 'Valid CVV required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      setIsProcessing(true);
      
      // Store order details before clearing cart
      const orderDetails = {
        items: cartItems,
        subtotal,
        shipping,
        tax,
        total,
        shippingAddress: shippingData,
        paymentMethod: paymentMethod === 'card' ? `**** **** **** ${formData.cardNumber.slice(-4)}` : 'PayPal',
        orderNumber: 'CV-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        orderDate: new Date().toISOString(),
      };
      localStorage.setItem('chillverify-order', JSON.stringify(orderDetails));
      
      setTimeout(() => {
        clearCart();
        localStorage.removeItem('chillverify-shipping');
        navigate('/checkout/confirmation');
      }, 2000);
    }
  };

  const handlePayPal = () => {
    setIsProcessing(true);
    
    // Store order details before clearing cart
    const orderDetails = {
      items: cartItems,
      subtotal,
      shipping,
      tax,
      total,
      shippingAddress: shippingData,
      paymentMethod: 'PayPal',
      orderNumber: 'CV-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
      orderDate: new Date().toISOString(),
    };
    localStorage.setItem('chillverify-order', JSON.stringify(orderDetails));
    
    clearCart();
    navigate('/checkout/confirmation');
  };

  // Redirect to cart if empty and not processing
  if (cartItems.length === 0 && !isProcessing) {
    navigate('/cart');
    return null;
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="checkout-page">
      {/* Progress Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '16px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <ProgressIndicator currentIndex={1} spaceEqually>
                <ProgressStep label="Shipping" />
                <ProgressStep label="Payment" />
                <ProgressStep label="Confirmation" />
              </ProgressIndicator>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 0 32px' }}>
        <Grid>
          {/* Payment Form */}
          <Column lg={10} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', padding: '10px' }}>
                  <Wallet size={20} style={{ color: '#ffffff' }} />
                </div>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Payment Method</h2>
              </div>

              {/* Payment Method Selection */}
              <div style={{ marginBottom: '20px' }}>
                <RadioButtonGroup
                  name="payment-method"
                  valueSelected={paymentMethod}
                  onChange={(value) => setPaymentMethod(value)}
                  orientation="vertical"
                >
                  <RadioButton id="card" value="card" labelText="Credit / Debit Card" />
                  <RadioButton id="paypal" value="paypal" labelText="PayPal" />
                </RadioButtonGroup>
              </div>

              {paymentMethod === 'card' && (
                <form onSubmit={handleSubmit} data-testid="payment-form">
                  <p style={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px' }}>Card Details</p>

                  {/* Card Number */}
                  <div style={{ marginBottom: '12px' }}>
                    <TextInput
                      id="cardNumber"
                      name="cardNumber"
                      labelText="Card Number"
                      placeholder="1234 5678 9012 3456"
                      value={formData.cardNumber}
                      onChange={(e) => setFormData({ ...formData, cardNumber: formatCardNumber(e.target.value) })}
                      invalid={!!errors.cardNumber}
                      invalidText={errors.cardNumber}
                      maxLength={19}
                      size="lg"
                      data-testid="card-number"
                    />
                  </div>

                  {/* Cardholder Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <TextInput
                      id="cardName"
                      name="cardName"
                      labelText="Cardholder Name"
                      placeholder="John Doe"
                      value={formData.cardName}
                      onChange={handleChange}
                      invalid={!!errors.cardName}
                      invalidText={errors.cardName}
                      size="lg"
                      data-testid="card-name"
                    />
                  </div>

                  {/* Expiry and CVV */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                    <TextInput
                      id="expiry"
                      name="expiry"
                      labelText="Expiry Date"
                      placeholder="MM/YY"
                      value={formData.expiry}
                      onChange={handleChange}
                      invalid={!!errors.expiry}
                      invalidText={errors.expiry}
                      maxLength={5}
                      size="lg"
                      data-testid="expiry"
                    />
                    <TextInput
                      id="cvv"
                      name="cvv"
                      labelText="CVV"
                      placeholder="123"
                      value={formData.cvv}
                      onChange={handleChange}
                      invalid={!!errors.cvv}
                      invalidText={errors.cvv}
                      maxLength={4}
                      type="password"
                      size="lg"
                      data-testid="cvv"
                    />
                  </div>

                  {/* Save Card */}
                  <div style={{ marginBottom: '16px' }}>
                    <Checkbox
                      id="saveCard"
                      labelText="Save card for future purchases"
                      checked={formData.saveCard}
                      onChange={(_, { checked }) => setFormData({ ...formData, saveCard: checked })}
                    />
                  </div>

                  {/* Security Notice */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: bgPrimary,
                    borderRadius: '8px',
                    marginBottom: '20px',
                  }}>
                    <Locked size={16} style={{ color: '#24a148' }} />
                    <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                      Your payment information is encrypted and secure
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Button
                      kind="primary"
                      type="submit"
                      size="lg"
                      disabled={loading}
                      style={{ width: '100%' }}
                      data-testid="place-order"
                    >
                      {loading ? 'Processing...' : `Place Order - $${total.toFixed(2)}`}
                    </Button>
                    <Button
                      kind="secondary"
                      size="lg"
                      renderIcon={ArrowLeft}
                      onClick={() => navigate('/checkout/shipping')}
                      style={{ width: '100%' }}
                      data-testid="back-to-shipping"
                    >
                      Back to Shipping
                    </Button>
                  </div>
                </form>
              )}

              {paymentMethod === 'paypal' && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <p style={{ color: textSecondary, marginBottom: '20px', fontSize: '0.875rem' }}>
                    You will be redirected to PayPal to complete your purchase.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Button
                      kind="primary"
                      size="lg"
                      onClick={handlePayPal}
                      style={{ width: '100%' }}
                    >
                      Continue to PayPal - ${total.toFixed(2)}
                    </Button>
                    <Button
                      kind="secondary"
                      size="lg"
                      renderIcon={ArrowLeft}
                      onClick={() => navigate('/checkout/shipping')}
                      style={{ width: '100%' }}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </Tile>
          </Column>

          {/* Order Summary & Shipping Info */}
          <Column lg={6} md={8} sm={4}>
            {/* Order Summary */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Order Summary</h3>
              
              {/* Cart Items */}
              <div style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: '12px', marginBottom: '12px' }}>
                {cartItems.map((item) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.875rem' }}>
                    <span style={{ color: textSecondary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                      {item.quantity}x {item.name}
                    </span>
                    <span style={{ color: textPrimary, fontWeight: 500 }}>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: textSecondary }}>Subtotal</span>
                  <span style={{ color: textPrimary }}>${subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: textSecondary }}>Shipping</span>
                  <span style={{ color: shipping === 0 ? '#24a148' : textPrimary }}>
                    {shipping === 0 ? 'Included' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: textSecondary }}>GST (10%)</span>
                  <span style={{ color: textPrimary }}>${tax.toFixed(2)}</span>
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: textPrimary, fontWeight: 600 }}>Total</span>
                  <span style={{ color: '#0f62fe', fontSize: '1.25rem', fontWeight: 600 }}>${total.toFixed(2)}</span>
                </div>
              </div>
            </Tile>

            {/* Shipping Address */}
            {shippingData && (
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Shipping To</h3>
                  <Button kind="ghost" size="sm" onClick={() => navigate('/checkout/shipping')}>Edit</Button>
                </div>
                <p style={{ color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                  {shippingData.firstName} {shippingData.lastName}<br />
                  {shippingData.address}<br />
                  {shippingData.apartment && <>{shippingData.apartment}<br /></>}
                  {shippingData.city}, {shippingData.state} {shippingData.zipCode}<br />
                  United States
                </p>
              </Tile>
            )}
          </Column>
        </Grid>
      </div>
    </div>
  );
};

export default CheckoutPage;
